import React, { useEffect, useMemo, useState } from 'react'
import { backendAPI } from '@/utils/api'
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

type Client = {
  id: string
  name: string
  email?: string
  phone?: string
  status?: 'active' | 'prospect' | 'inactive'
  industry?: string
  created_at?: string
}

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (search) p.search = search
    if (status) p.status = status
    p.page = '1'
    p.limit = '25'
    p.sort_by = 'created_at'
    p.sort_order = 'desc'
    return p
  }, [search, status])

  useEffect(() => {
    let mounted = true
    setIsLoading(true)
    backendAPI
      .getClients?.(params as any)
      .then(async (res: any) => {
        // Worker returns { data, pagination }
        const rows = res?.data || []
        if (mounted) setClients(rows)
      })
      .catch(() => toast.error('Failed to load clients'))
      .finally(() => mounted && setIsLoading(false))
    return () => {
      mounted = false
    }
  }, [params])

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">CRM with filtering and search</p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <PlusIcon className="h-5 w-5" /> New Client
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="prospect">Prospect</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td className="px-6 py-10 text-center text-gray-500" colSpan={5}>Loading...</td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td className="px-6 py-10 text-center text-gray-500" colSpan={5}>No clients found</td>
              </tr>
            ) : (
              clients.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{c.email || '-'}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      c.status === 'active' ? 'bg-green-100 text-green-800' :
                      c.status === 'prospect' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {c.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{c.industry || '-'}</td>
                  <td className="px-6 py-4 text-right text-sm">
                    <button className="text-blue-600 hover:text-blue-800">View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ClientsPage
